import { styled } from '@mui/material/styles';
import type {
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
} from '@mui/x-data-grid-pro';
import { Analytics } from '@/shared/lib';
import type { ColumnsConfig } from '@/shared/types';
import type { MinMaxData } from '@/shared/ui';
import {
  getColumnHeaderHeight,
  DataGrid as SharedDataGrid,
  useDataGridListeners,
} from '@/shared/ui';
import { useData } from './useData';
import { useTableProps } from './useTableProps';

const DataGrid = styled(SharedDataGrid)(({ theme }) => ({
  '& .dimensionCell + .metricCell, & .dimensionHeader + .metricHeader': {
    boxShadow: 'inset 8px 0 8px -8px #00234033',
  },

  '& .dimensionCell': {
    padding: `0 ${theme.spacing(1.25)}`,
    flexShrink: 0,
  },

  '& .columnGroup': {
    display: 'none',
  },
}));

interface Props {
  isReadonly?: boolean;
  rowHeight: number;
  columnsWidth: Record<string, number | undefined>;
  columnsConfig: ColumnsConfig;
  dimensions: string[];
  metric: string;
  minMax?: MinMaxData[string];
  reorderDimensions?: (info: {
    id: string;
    oldIndex: number;
    newIndex: number;
  }) => void;
  updateColumnsWidth?: (columnName: string, newWidth?: number) => void;
}

export function DataTable({
  isReadonly,
  rowHeight,
  columnsWidth,
  columnsConfig,
  dimensions,
  metric,
  minMax,
  reorderDimensions,
  updateColumnsWidth,
}: Props) {
  const { rows, columns: dataColumns, loading, getNextPage } = useData();
  const { columnGroupingModel, columns } = useTableProps({
    columnsWidth,
    columnsConfig,
    dimensions,
    metric,
    dataColumns,
    minMax,
    isReadonly,
  });

  useDataGridListeners();

  const handleColumnOrderChange = (params: GridColumnOrderChangeParams) => {
    const { column } = params;
    const oldIndex = params.oldIndex;
    const targetIndex = params.targetIndex;

    if (oldIndex === targetIndex) {
      return;
    }

    reorderDimensions?.({
      id: column.field,
      oldIndex: oldIndex,
      newIndex: targetIndex,
    });
    Analytics.sendFeatureUsage('data_table', 'reorder_column');
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const { width, field } = params.colDef;

    updateColumnsWidth?.(field, width);
  };

  return (
    <DataGrid
      disableColumnResize={isReadonly}
      columnHeaderHeight={getColumnHeaderHeight(rowHeight)}
      rowHeight={rowHeight}
      loading={loading}
      columnGroupingModel={columnGroupingModel}
      columns={columns}
      rows={rows}
      getRowClassName={(params) => {
        return params.row.isTotal ? 'total' : '';
      }}
      onRowsScrollEnd={getNextPage}
      onColumnOrderChange={handleColumnOrderChange}
      onColumnWidthChange={handleColumnWidthChange}
    />
  );
}
